module Meevo.Plugins.MeevoMessenger {
    'use strict';

    // GSM-7 character set - characters that can be encoded in 7 bits
    const GSM7_CHARACTERS = new Set([
        // Basic Latin characters (0x20-0x7F minus some exceptions)
        ' ', '!', '"', '#', '�', '%', '&', "'", '(', ')', '*', '+', ',', '-', '.', '/',
        '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ':', ';', '<', '=', '>', '?',
        '@', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O',
        'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', '[', '\\', ']', '^', '_',
        '`', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o',
        'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', '{', '|', '}', '~',

        // Additional GSM-7 characters
        '�', '�', '�', '�', '�', '�', '�', '�', '�', '�', '�', '�',
        '�', '�', '�', '�', '�', '�', '�', '�', '�', '�',
        '�', '�', '�', '�', '�', '�',

        // Control characters
        '\n', '\r', '\f'
    ]);

    // GSM-7 extended characters (require escape sequence, count as 2 characters)
    const GSM7_EXTENDED_CHARACTERS = new Set([
        '|', '^', '�', '{', '}', '[', ']', '~', '\\'
    ]);

    interface SmsLengthInfo {
        encoding: 'GSM-7' | 'UCS-2';
        characterCount: number;
        byteCount: number;
        remainingCharacterCount: number;
        segmentCount: number;
    }

    /**
     * Calculates SMS length and encoding information for a given string
     */
    export function calculateSmsLength(input: string, maxIputLength: number): SmsLengthInfo {
        let gsm7Length = 0;
        let canUseGsm7 = true;

        // Check each character to determine encoding and calculate length
        for (const char of input) {
            if (GSM7_CHARACTERS.has(char)) {
                gsm7Length += 1;
            } else if (GSM7_EXTENDED_CHARACTERS.has(char)) {
                gsm7Length += 2; // Extended characters require escape sequence
            } else {
                canUseGsm7 = false;
                break;
            }
        }

        if (canUseGsm7) {
            // GSM-7 encoding
            const singleSmsLimit = 160;
            const concatenatedSmsLimit = 153; // 160 - 7 bytes for concatenation header

            const segmentCount = gsm7Length <= singleSmsLimit ? 1 :
                Math.ceil(gsm7Length / concatenatedSmsLimit);

            return {
                encoding: 'GSM-7',
                characterCount: gsm7Length,
                byteCount: Math.ceil(gsm7Length * 7 / 8), // GSM-7 uses 7 bits per character
                remainingCharacterCount: maxIputLength - gsm7Length,
                segmentCount,
            };
        } else {
            // Unicode (UCS-2) encoding
            // Count code units properly (emoji and some characters use multiple code units)
            const unicodeLength = [...<any>input].length; // This correctly counts grapheme clusters
            const codeUnits = input.length; // This counts UTF-16 code units

            const singleSmsLimit = 70;
            const concatenatedSmsLimit = 67; // 70 - 3 characters for concatenation header

            // For SMS, we need to count code units (what carriers actually count)
            const segmentCount = codeUnits <= singleSmsLimit ? 1 :
                Math.ceil(codeUnits / concatenatedSmsLimit);

            return {
                encoding: 'UCS-2',
                characterCount: codeUnits, // Use code units for SMS counting
                byteCount: codeUnits * 2, // UCS-2 uses 2 bytes per code unit
                remainingCharacterCount: maxIputLength - codeUnits,
                segmentCount,
            };
        }
    }
    
}